import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import AddressBookButton from "@/components/AddressBookButton";
import AdminUserAddressBookButton from "@/components/AdminUserAddressBookButton";
import { CARRIERS, getCarrier } from "@/lib/carriers";
import { sortCheapest } from "@/lib/quoteEngine";
import { fetchQuotesProgressive } from "@/lib/progressiveQuote";
import { formatMXN } from "@/lib/format";
import { recommendPackageType } from "@/lib/packageTypeRules";
import { Search, Package2, Loader2, Truck, ArrowRight, Ruler, AlertCircle } from "lucide-react";
import MobileSelect from "@/components/MobileSelect";
import { COUNTRIES } from "@/lib/quoteCountries";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import QuoteResults from "@/components/quote/QuoteResults";
import SelectedRateView from "@/components/quote/SelectedRateView";
import { useTurnstile } from "@/hooks/useTurnstile";

const DRAFT_KEY = "bhgc_draft_quote";

export function getDraftQuote() {
  try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
}
export function clearDraftQuote() { sessionStorage.removeItem(DRAFT_KEY); }

export default function LandingQuote() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Los iconos de libreta de direcciones (personal y de admin) solo se muestran
  // si el visitante tiene sesión activa. En el cotizador público anónimo de la
  // landing no se renderizan.
  const showAddressBook = !!user;
  const [origin, setOrigin] = useState("");
  const [dest, setDest] = useState("");
  const [originCountry, setOriginCountry] = useState("México");
  const [destCountry, setDestCountry] = useState("México");
  const [weight, setWeight] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [showDims, setShowDims] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [chosen, setChosen] = useState(null);
  const [srsimiPending, setSrsimiPending] = useState(false);
  const [quoteSeq, setQuoteSeq] = useState(0);
  const cancelQuote = useRef(null);
  const ts = useTurnstile("landing_quote");

  // El cotizador de la landing NO autollena el CP desde la libreta de
  // direcciones. Las direcciones guardadas solo se usan al seleccionarlas
  // manualmente desde el botón "Libreta de Direcciones" (AddressBookButton).
  const lastOrigin = useRef("");
  const lastDest = useRef("");

  const volWeight = (() => {
    const l = parseFloat(length), w = parseFloat(width), h = parseFloat(height);
    if (!isFinite(l) || !isFinite(w) || !isFinite(h) || l <= 0 || w <= 0 || h <= 0) return null;
    return Number(((l * w * h) / 5000).toFixed(2));
  })();

  // Aviso de tipo de envío según dimensiones/peso (Sobre→Paquete→Tarima). El
  // cotizador público no tiene selector de tipo, así que solo se avisa cuando
  // las medidas exceden el límite de Paquete y requerirían Tarima al generar.
  const pkgNotice = (() => {
    const l = parseFloat(length), w = parseFloat(width), h = parseFloat(height);
    if (!(isFinite(l) && l > 0) || !(isFinite(w) && w > 0) || !(isFinite(h) && h > 0)) return null;
    const rec = recommendPackageType("paquete", parseFloat(weight) || 0, { length: l, width: w, height: h });
    return rec.suggested === "tarima" ? "Tus medidas exceden el límite de Paquete. Esta ruta se cotizará y generará como Tarima al crear tu cuenta." : null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!origin || !dest) { setError("Ingresa ambos códigos postales."); return; }
    const v = await ts.verify();
    if (!v.success) { setError("Verificación anti-bot fallida. Inténtalo de nuevo."); return; }
    setLoading(true);
    setRates([]);
    setChosen(null);
    setOpen(true);
    const payload = {
      origin_cp: origin, dest_cp: dest,
      origin_country: originCountry, dest_country: destCountry,
      weight_kg: parseFloat(weight) || 1,
      length_cm: parseFloat(length) || 0,
      width_cm: parseFloat(width) || 0,
      height_cm: parseFloat(height) || 0
    };
    if (cancelQuote.current) cancelQuote.current();
    setSrsimiPending(true);
    setQuoteSeq(s => s + 1);
    cancelQuote.current = fetchQuotesProgressive("publicQuoteShipments", payload, {
      onRates: (all) => {
        setRates(sortCheapest(all));
        if (all.length) { setError(""); setLoading(false); }
      },
      onSrSimiDone: () => setSrsimiPending(false),
      onComplete: () => { setLoading(false); setSrsimiPending(false); },
      onError: () => {},
      webQuote: true,
      turnstilePass: ts.getPass()
    });
  };

  const choose = (rate) => {
    const draft = {
      origin_cp: origin, dest_cp: dest,
      origin_country: originCountry, dest_country: destCountry,
      weight_kg: parseFloat(weight) || 1,
      length_cm: parseFloat(length) || 0,
      width_cm: parseFloat(width) || 0,
      height_cm: parseFloat(height) || 0,
      selected: rate
    };
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    setChosen(rate);
  };

  const goTo = (path) => {
    setOpen(false);
    navigate(path);
  };

  const field = "bg-ink border border-ink-border px-3 py-2.5 text-sm text-ink-text placeholder:text-ink-muted focus:border-cyan focus:outline-none w-full min-w-0";

  return (
    <>
      <form onSubmit={submit} className="surface p-4 lg:p-5 max-w-md">
        <div className="flex items-center gap-2 mb-4">
          <Package2 className="w-4 h-4 text-cyan" />
          <h2 className="font-display font-bold text-sm uppercase tracking-wide">Cotiza tu envío gratis</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="label flex items-center gap-1.5 mb-1.5"><span>CP Origen</span>
              {showAddressBook && <AddressBookButton mode="sender" onPick={(a) => { if (a.cp) { lastOrigin.current = String(a.cp); setOrigin(String(a.cp)); if (a.country) setOriginCountry(a.country); } }} title="Usar dirección guardada (origen)" />}
              {showAddressBook && <AdminUserAddressBookButton mode="sender" onPick={(a) => { if (a.cp) { lastOrigin.current = String(a.cp); setOrigin(String(a.cp)); if (a.country) setOriginCountry(a.country); } }} title="Libreta de usuarios (origen)" />}
            </div>
            <input className={field} value={origin} onChange={e => setOrigin(e.target.value)} inputMode="numeric" maxLength={originCountry === "México" ? 5 : 10} placeholder="00000" required />
          </div>
          <div>
            <label className="label block mb-1.5">País Origen</label>
            <MobileSelect className={field} value={originCountry} onChange={setOriginCountry} options={COUNTRIES.map(c => ({ value: c, label: c }))} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <div className="label flex items-center gap-1.5 mb-1.5"><span>CP Destino</span>
              {showAddressBook && <AddressBookButton mode="recipient" onPick={(a) => { if (a.cp) { lastDest.current = String(a.cp); setDest(String(a.cp)); if (a.country) setDestCountry(a.country); } }} title="Usar dirección guardada (destino)" />}
              {showAddressBook && <AdminUserAddressBookButton mode="recipient" onPick={(a) => { if (a.cp) { lastDest.current = String(a.cp); setDest(String(a.cp)); if (a.country) setDestCountry(a.country); } }} title="Libreta de usuarios (destino)" />}
            </div>
            <input className={field} value={dest} onChange={e => setDest(e.target.value)} inputMode="numeric" maxLength={destCountry === "México" ? 5 : 10} placeholder="00000" required />
          </div>
          <div>
            <label className="label block mb-1.5">País Destino</label>
            <MobileSelect className={field} value={destCountry} onChange={setDestCountry} options={COUNTRIES.map(c => ({ value: c, label: c }))} />
          </div>
        </div>
        <div className="mt-3">
          <label className="label block mb-1.5">Peso (kg)</label>
          <input className={field} value={weight} onChange={e => setWeight(e.target.value)} type="number" step="0.1" min="0.1" placeholder="0.5" required />
          {volWeight != null && (
            <div className="mt-1.5 surface-inset px-2.5 py-1.5 flex items-center justify-between text-[0.7rem]">
              <span className="text-ink-muted">Peso volumétrico (L×A×H ÷ 5000)</span>
              <span className="font-mono font-bold text-cyan">{volWeight} kg</span>
            </div>
          )}
        </div>

        <button type="button" onClick={() => setShowDims(v => !v)}
          className="mt-3 flex items-center gap-1.5 text-xs text-cyan hover:underline">
          <Ruler className="w-3.5 h-3.5" /> {showDims ? "Ocultar dimensiones" : "Agregar dimensiones (cm)"}
        </button>
        {showDims && (
          <div className="grid grid-cols-3 gap-3 mt-2">
            <input className={field} value={length} onChange={e => setLength(e.target.value)} type="number" step="0.1" placeholder="Largo" />
            <input className={field} value={width} onChange={e => setWidth(e.target.value)} type="number" step="0.1" placeholder="Ancho" />
            <input className={field} value={height} onChange={e => setHeight(e.target.value)} type="number" step="0.1" placeholder="Alto" />
          </div>
        )}
        {pkgNotice && (
          <div className="mt-2 surface-inset px-2.5 py-1.5 flex items-start gap-1.5 text-[0.7rem] text-warning border-l-2 border-warning">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{pkgNotice}</span>
          </div>
        )}

        {ts.widget}
        <button type="submit" disabled={loading}
          className="ai-push mt-4 w-full flex items-center justify-center gap-2 bg-cyan hover:bg-cyan-hover disabled:opacity-50 text-ink py-3 font-display font-bold uppercase tracking-wide text-sm glow-cyan">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? "Cotizando…" : "Ver tarifas"}
        </button>
        <p className="text-[0.65rem] text-ink-muted mt-2 text-center">
          Compara {CARRIERS.length}+ paqueterías en segundos. Sin costo y sin registrarte.
        </p>
      </form>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ink-surface border-ink-border max-w-2xl p-0">
          <div className="flex items-center justify-between px-5 py-4 border-b border-ink-border">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-cyan" />
              <h3 className="font-display font-bold text-sm uppercase">Tus tarifas · {origin}{originCountry !== "México" ? ` ${originCountry}` : ""} → {dest}{destCountry !== "México" ? ` ${destCountry}` : ""}</h3>
            </div>
          </div>

          <div className="p-5 max-h-[70dvh] overflow-y-auto">
            {loading && (
              <div className="flex flex-col items-center gap-3 py-12 text-ink-muted">
                <Loader2 className="w-6 h-6 text-cyan animate-spin" />
                <span className="text-sm">Comparando {CARRIERS.length}+ paqueterías…</span>
              </div>
            )}

            {!loading && chosen && (
              <SelectedRateView
                chosen={chosen}
                user={user}
                onCapture={() => goTo("/panel")}
                onRegister={() => goTo("/register?returnTo=%2Fpanel")}
                onLogin={() => goTo("/login?returnTo=%2Fpanel")}
                onBack={() => setChosen(null)}
              />
            )}

            {!loading && !chosen && error && (
              <div className="text-center py-10 text-ink-muted text-sm">{error}</div>
            )}

            {!loading && !chosen && !error && rates.length > 0 && (
              <>
                <div className="flex items-center gap-1.5 text-[0.7rem] text-ink-muted mb-3">
                  <Truck className="w-3.5 h-3.5 text-cyan" /> {rates.length} opciones · ordenado por menor precio
                </div>
                <QuoteResults
                  rates={rates}
                  hideUnavailable
                  srsimiPending={srsimiPending}
                  onSelect={choose}
                  quoteId={String(quoteSeq)}
                />
                <div className="mt-4 surface-inset p-3 text-center text-xs text-ink-muted">
                  Al elegir una tarifa creas tu cuenta para continuar con la captura de tu guía y el pago.
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}